import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAnalyticsContext } from "@/modules/analytics/context/analytics.context";
import { MixpanelTrackingUtil } from "@/modules/analytics/utils/mixpanel-tracking.util";

/**
 * Tracks page views and route changes using Mixpanel.
 * - Includes user info (name + rank) when available.
 * - Persists the previous path across refreshes using sessionStorage.
 * - Prevents duplicate tracking on initial render.
 */
export function usePageViewTracking() {
  const location = useLocation();
  const { analytics, ready, userInfo } = useAnalyticsContext();

  const previousPath = useRef<string | null>(null);
  const hasTrackedInitial = useRef(false);

  useEffect(() => {
    if (!analytics || !ready || !userInfo) return;

    const currentPath = location.pathname;
    const storedPrevious = sessionStorage.getItem("previousPath");

    const trackingPayload = {
      hash: location.hash,
      search: location.search,
      fullUrl: window.location.href,
      isInitialLoad: !storedPrevious,
    };

    if (!hasTrackedInitial.current) {
      MixpanelTrackingUtil.trackPageView(
        analytics,
        currentPath,
        storedPrevious,
        trackingPayload,
        userInfo
      );
      hasTrackedInitial.current = true;
    } else if (previousPath.current !== currentPath) {
      MixpanelTrackingUtil.trackPageView(
        analytics,
        currentPath,
        previousPath.current,
        { ...trackingPayload, isInitialLoad: false },
        userInfo
      );
    }

    sessionStorage.setItem("previousPath", currentPath);
    previousPath.current = currentPath;
  }, [location, analytics, ready, userInfo]);
}
